export type MissionEvidenceRowKind =
  | 'reason'
  | 'composition'
  | 'signal'
  | 'data_source'
  | 'evidence_ref'
  | 'entity';

export interface MissionEvidenceRow {
  kind: MissionEvidenceRowKind;
  label: string;
  value: string;
}

export interface MissionEvidenceProjection {
  missionId: string;
  rows: MissionEvidenceRow[];
  empty: boolean;
}

const MAX_SAFE_TEXT_LENGTH = 280;
const UNSAFE_LABEL_PATTERN = /(?:token|secret|credential|password|prompt|completion|response|body|raw|hash|wallet)/i;
const OPAQUE_ID_PATTERN = /(?:^|[:/_-])(?:[a-f0-9]{16,}|[A-Za-z0-9_-]{24,})(?:$|[:/_-])/;

function safeText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (UNSAFE_LABEL_PATTERN.test(trimmed)) return null;
  if (OPAQUE_ID_PATTERN.test(trimmed)) return null;
  return trimmed.length > MAX_SAFE_TEXT_LENGTH
    ? `${trimmed.slice(0, MAX_SAFE_TEXT_LENGTH - 1)}…`
    : trimmed;
}

function safeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(safeText)
    .filter((item): item is string => Boolean(item));
}

function pushText(rows: MissionEvidenceRow[], kind: MissionEvidenceRowKind, label: string, value: unknown): void {
  const safe = safeText(value);
  if (!safe) return;
  rows.push({ kind, label, value: safe });
}

export function projectMissionEvidence(mission: Record<string, unknown> | null | undefined): MissionEvidenceProjection {
  const rows: MissionEvidenceRow[] = [];
  const missionId = safeText(mission?.id) ?? 'unknown';

  if (!mission) {
    return { missionId, rows, empty: true };
  }

  pushText(rows, 'reason', 'Recommendation reason', mission.reason);
  pushText(rows, 'composition', 'Composition reasoning', mission.compositionReasoning);

  for (const signal of safeStringList(mission.signalSummaries)) {
    rows.push({ kind: 'signal', label: 'Signal summary', value: signal });
  }

  for (const source of safeStringList(mission.dataSources)) {
    rows.push({ kind: 'data_source', label: 'Data source', value: source });
  }

  const evidenceRefs = Array.isArray(mission.evidenceChain)
    ? mission.evidenceChain
    : mission.evidenceRefs;
  for (const evidenceRef of safeStringList(evidenceRefs)) {
    rows.push({ kind: 'evidence_ref', label: 'Evidence reference', value: evidenceRef });
  }

  pushText(rows, 'entity', 'Source entity', mission.entitySource);
  pushText(rows, 'entity', 'Entity type', mission.entityType);
  pushText(rows, 'entity', 'Entity category', mission.entityCategory);

  if (typeof mission.entityConfidence === 'number' && Number.isFinite(mission.entityConfidence)) {
    rows.push({
      kind: 'entity',
      label: 'Confidence',
      value: `${Math.round(Math.max(0, Math.min(1, mission.entityConfidence)) * 100)}%`,
    });
  }

  return {
    missionId,
    rows,
    empty: rows.length === 0,
  };
}
